import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { getEnumName } from "@racoon/lib";
import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import { useListDriverOnShiftQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useListPickupLocationGroupsQuery } from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { useListContractsQuery } from "~/shared/api/contract/contract.grpc";
import { useLocationSchedules } from "~/shared/api/container-group-schedule/use-location-schedules";
import { schedulesByCgId } from "~/shared/lib/container-group-schedule";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { DateValue } from "~/shared/lib/analytics-utils";
import { getJobFactContainerCount, getJobFactVolume } from "~/shared/lib/job-metrics";
import { idKey, periodBounds, personName } from "./job-report";
import type { ReportFilterValues } from "./use-report-filters";

// The report rows, built in the browser from the list endpoints. Reports asking for the same
// list share its cache, so a tab re-uses what another tab already loaded.

type Timestamp = ConnectProto.Job["createdAt"];

/** A job as the reports read it. */
export interface ReportJob {
  id: bigint;
  status: ConnectProto.JobStatus;
  /** "JOB_TYPE_*" key of the job's type. */
  jobType?: string;
  twStartAt?: Timestamp;
  factEndAt?: Timestamp;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  serviceReceiverId: bigint;
  serviceReceiverName?: string;
  serviceReceiverBin?: string;
  pickupLocationId: bigint;
  pickupLocationName?: string;
  containerGroupId?: bigint;
  /** The group's container type; legacy jobs without a group carry their own. */
  containerTypeId?: bigint;
  containerTypeName?: string;
  containerTypeVolume?: number;
  /** Unset for legacy jobs without a container group. */
  numberOfContainers?: number;
  /** Containers a completed job serviced; unset on other jobs. */
  factContainerCount?: number;
  /** The ТТН's actual volume: declared fill coefficients × the container volume. */
  factTotalVolume?: number;
  contractDealNumber?: string;
  driverId?: bigint;
  driverName?: string;
  truckId?: bigint;
  truckPlateNumber?: string;
  /** Crew members of the shift with the loader role, comma-separated. */
  loaderNames?: string;
  hasPhotoReport: boolean;
  driverComment?: string;
  serviceProviderName?: string;
}

/** A shift as the reports read it. */
export interface ReportDriverOnShift {
  id: bigint;
  driverId?: bigint;
  driverName?: string;
  truckId?: bigint;
  truckPlateNumber?: string;
  twStartAt?: Timestamp;
  startedAt?: Timestamp;
  createdAt?: Timestamp;
  crewIds: bigint[];
}

/** A container group as the reports read it. */
export interface ReportContainerGroup {
  id: bigint;
  pickupLocationId: bigint;
  pickupLocationName: string;
  ownerOrganizationId: bigint;
  ownerOrganizationName?: string;
  containerTypeId?: bigint;
  containerTypeName?: string;
  numberOfContainers?: number;
  contractDealNumber?: string;
  contractEndAt?: Timestamp;
  /** Its rows in the schedule table. */
  schedules: ConnectProto.ContainerGroupSchedule[];
  /** The responsible zone in order; empty when the group has none. */
  zone: { id: string; name: string }[];
  /** The contract's provider, who serves every schedule of a group without a zone. */
  contractProviderName?: string;
  pickupLocationGroups: { name: string; jobType: string }[];
}

type JobFilters = Partial<
  Pick<ReportFilterValues, "serviceReceiver" | "pickupLocation" | "driver" | "truck">
>;

interface JobOptions {
  /** The job's time column the period applies to; tw_start_at by default. */
  timeField?: "fact_end_at";
  /** Loader names from the period's shifts. */
  withLoaders?: boolean;
  /** Contract deal numbers from the organization's contracts. */
  withContracts?: boolean;
}

// Filter ids come from the URL: a malformed one is ignored instead of throwing in BigInt.
const urlId = (id?: string) => (id && /^\d+$/.test(id) ? BigInt(id) : undefined);
const idList = (id?: string) => {
  const parsed = urlId(id);
  return parsed === undefined ? [] : [parsed];
};
const contractNumber = (contract: ConnectProto.Contract) =>
  contract.dealNumber || contract.name || "";

const useOrgId = () => {
  const { activeCompanyId } = useAuthSession();
  return { orgId: BigInt(activeCompanyId ?? 0), skip: !activeCompanyId };
};

// Built the same way everywhere, so reports asking for the same jobs share one cache entry.
const jobListArgs = (
  orgId: bigint,
  startDate: DateValue,
  endDate: DateValue,
  jobStatuses: ConnectProto.JobStatus[],
  filters: JobFilters,
  timeField: string,
) => ({
  serviceProviderIds: [orgId],
  jobStatuses,
  timeFilter: { ...periodBounds(startDate, endDate), field: timeField },
  serviceReceiverIds: idList(filters.serviceReceiver),
  pickupLocationIds: idList(filters.pickupLocation),
  // driver_ids matches the job's shift driver; the older driver_id is kept for existing callers.
  driverIds: idList(filters.driver),
});

const shiftListArgs = (orgId: bigint, startDate: DateValue, endDate: DateValue) => ({
  ownerOrganizationIds: [orgId],
  dateTimeFilter: { ...periodBounds(startDate, endDate), field: "tw_start_at" },
});

// A job's shift can start before the period (an overnight shift, or a job dated by its
// completion), so the loaders' shift list reaches back to the earliest shift the jobs are on.
const loaderShiftArgs = (
  orgId: bigint,
  jobs: ConnectProto.Job[],
  startDate: DateValue,
  endDate: DateValue,
) => {
  const period = periodBounds(startDate, endDate);
  const from = jobs.reduce((earliest, job) => {
    const start = job.driverOnShift?.twStartAt;
    return start && start.seconds < earliest.seconds ? start : earliest;
  }, period.from);
  return {
    ownerOrganizationIds: [orgId],
    dateTimeFilter: { from, to: period.to, field: "tw_start_at" },
  };
};

const loaderNames = (shift: ConnectProto.DriverOnShift) =>
  (shift.crew ?? [])
    .filter((user) => user.role === ConnectProto.UserRole.PROVIDER_LOADER)
    .map((user) => personName(user))
    .sort((a, b) => a.localeCompare(b))
    .join(", ");

/**
 * A completed job's facts as its photo report shows them: one container per declared
 * fill, and without fills every planned container counted full, so the fact volume is
 * the nominal one. A saved volume wins; 0 here means no data, so it stays unset.
 * The fills are counted here rather than through `getJobFactContainerCount`: that is the
 * reported count (num_of_container), which the backend raises on «+» but never lowers on «−».
 */
export const completedJobFacts = (
  job: ConnectProto.Job,
): Pick<ReportJob, "factContainerCount" | "factTotalVolume"> =>
  COMPLETED_STATUSES.includes(job.status)
    ? {
        factContainerCount:
          job.containerCoefficients.length || getJobFactContainerCount(job) || undefined,
        factTotalVolume: job.factTotalVolume ?? (getJobFactVolume(job) || undefined),
      }
    : { factContainerCount: undefined, factTotalVolume: job.factTotalVolume };

/** Jobs in the period, placeholders left out. The truck filter keys on the job's shift. */
export function useReportJobs(
  startDate: DateValue,
  endDate: DateValue,
  jobStatuses: ConnectProto.JobStatus[],
  filters: JobFilters = {},
  { timeField, withLoaders = false, withContracts = false }: JobOptions = {},
) {
  const { orgId, skip } = useOrgId();
  const jobs = useListJobsQuery(
    jobListArgs(orgId, startDate, endDate, jobStatuses, filters, timeField ?? "tw_start_at"),
    { skip },
  );
  const shifts = useListDriverOnShiftQuery(
    loaderShiftArgs(orgId, jobs.data?.jobs ?? [], startDate, endDate),
    // Waits for the jobs: they decide how far back the shift list reaches.
    { skip: skip || !withLoaders || !jobs.data },
  );
  const contracts = useListContractsQuery(
    { serviceProviderIds: [orgId] },
    { skip: skip || !withContracts },
  );

  const rows = useMemo<ReportJob[]>(() => {
    const loadersByShift = new Map(
      (shifts.data?.driverOnShifts ?? []).map((shift) => [idKey(shift.id), loaderNames(shift)]),
    );
    const contractById = new Map(
      (contracts.data?.contracts ?? []).map((contract) => [
        idKey(contract.id),
        contractNumber(contract),
      ]),
    );
    return (jobs.data?.jobs ?? [])
      .filter((job) => !job.isPseudo)
      .filter((job) => !filters.truck || idKey(job.driverOnShift?.truckId) === filters.truck)
      .map((job): ReportJob => {
        const group = job.containerGroup;
        const type = group?.containerType ?? job.containerType;
        const shift = job.driverOnShift;
        const contractId = group?.contractId ?? job.pickupLocation?.contractId;
        return {
          id: job.id,
          status: job.status,
          jobType: getEnumName(ConnectProto.JobTypeSchema, job.type),
          twStartAt: job.twStartAt,
          factEndAt: job.factEndAt,
          createdAt: job.createdAt,
          updatedAt: job.updatedAt,
          serviceReceiverId: job.serviceReceiverId,
          serviceReceiverName: job.serviceReceiver?.name,
          serviceReceiverBin: job.serviceReceiver?.bin,
          pickupLocationId: job.pickupLocationId,
          pickupLocationName: job.pickupLocation?.name,
          containerGroupId: group?.id,
          containerTypeId: type?.id,
          containerTypeName: type?.name,
          containerTypeVolume: type?.volume,
          numberOfContainers: group?.numberOfContainers,
          ...completedJobFacts(job),
          contractDealNumber: contractById.get(idKey(contractId)),
          driverId: shift?.driverId,
          driverName: shift?.driver ? personName(shift.driver) : undefined,
          truckId: shift?.truckId,
          truckPlateNumber: shift?.truck?.plateNumber,
          loaderNames: loadersByShift.get(idKey(job.driverOnShiftId)),
          // Driver evidence, as the job list shows it: dispatcher-attached instructions also
          // live in file_reports and don't count.
          hasPhotoReport: Boolean(
            job.serviceStartedReportUrl ||
              job.serviceEndedReportUrl ||
              job.fileReports?.some(
                (file) => file.type !== ConnectProto.FileUploadType.JOB_INSTRUCTION,
              ),
          ),
          driverComment: job.driverComment,
          serviceProviderName: job.serviceProvider?.name,
        };
      });
  }, [jobs.data, shifts.data, contracts.data, filters.truck]);

  return {
    rows,
    isLoading: jobs.isLoading || jobs.isFetching || shifts.isLoading || contracts.isLoading,
    error: jobs.error ?? shifts.error ?? contracts.error,
  };
}

const COMPLETED_STATUSES = [
  ConnectProto.JobStatus.COMPLETED,
  ConnectProto.JobStatus.FORCE_COMPLETED,
];

/** Completed and force-completed jobs, dated by completion. */
export const useCompletedJobs = (
  startDate: DateValue,
  endDate: DateValue,
  filters?: JobFilters,
  options?: Omit<JobOptions, "timeField">,
) =>
  useReportJobs(startDate, endDate, COMPLETED_STATUSES, filters, {
    ...options,
    timeField: "fact_end_at",
  });

/** The organization's shifts planned in the period, subcontractors included. */
export function useReportDriverOnShifts(startDate: DateValue, endDate: DateValue) {
  const { orgId, skip } = useOrgId();
  const shifts = useListDriverOnShiftQuery(shiftListArgs(orgId, startDate, endDate), { skip });

  const rows = useMemo<ReportDriverOnShift[]>(
    () =>
      (shifts.data?.driverOnShifts ?? []).map((shift) => ({
        id: shift.id,
        driverId: shift.driverId,
        driverName: shift.driver ? personName(shift.driver) : undefined,
        truckId: shift.truckId,
        truckPlateNumber: shift.truck?.plateNumber,
        twStartAt: shift.twStartAt,
        startedAt: shift.startedAt,
        createdAt: shift.createdAt,
        crewIds: (shift.crew ?? []).map((user) => user.id),
      })),
    [shifts.data],
  );

  return { rows, isLoading: shifts.isLoading || shifts.isFetching, error: shifts.error };
}

const NO_LOCATIONS: ConnectProto.PickupLocation[] = [];

/** Container groups at the pickup locations the organization serves, with their schedule rows. */
export function useReportContainerGroups() {
  const { orgId, skip } = useOrgId();
  const locations = useListPickupLocationsQuery({ serviceProviderIds: [orgId] }, { skip });
  // The backend narrows the list to the groups the user may view, at a location whoever owns them.
  const schedules = useLocationSchedules(locations.data?.pickupLocations ?? NO_LOCATIONS, skip);
  const locationGroups = useListPickupLocationGroupsQuery(
    { ownerOrganizationIds: [orgId] },
    { skip },
  );

  const rows = useMemo<ReportContainerGroup[]>(() => {
    const schedulesByGroup = schedulesByCgId(schedules.schedules);
    const linksByGroup = new Map<string, { name: string; jobType: string }[]>();
    for (const plg of locationGroups.data?.pickupLocationGroups ?? []) {
      for (const link of plg.containerGroupLinks ?? []) {
        const key = idKey(link.containerGroupId);
        linksByGroup.set(key, [
          ...(linksByGroup.get(key) ?? []),
          { name: plg.name, jobType: getEnumName(ConnectProto.JobTypeSchema, link.jobType) ?? "" },
        ]);
      }
    }
    return (locations.data?.pickupLocations ?? []).flatMap((location) =>
      (location.containerGroups ?? []).map(
        (group): ReportContainerGroup => ({
          id: group.id,
          pickupLocationId: location.id,
          pickupLocationName: location.name,
          ownerOrganizationId: group.ownerOrganizationId,
          ownerOrganizationName: group.ownerOrganization?.name,
          containerTypeId: group.containerType?.id,
          containerTypeName: group.containerType?.name,
          numberOfContainers: group.numberOfContainers,
          contractDealNumber: group.contract ? contractNumber(group.contract) : undefined,
          contractEndAt: group.contract?.endAt,
          schedules: schedulesByGroup.get(idKey(group.id)) ?? [],
          zone: (group.serviceProviders ?? []).map((org) => ({
            id: String(org.id),
            name: org.name,
          })),
          contractProviderName: group.contract?.serviceProvider?.name,
          pickupLocationGroups: linksByGroup.get(idKey(group.id)) ?? [],
        }),
      ),
    );
  }, [locations.data, locationGroups.data, schedules.schedules]);

  // Every pickup location the list returned, container groups or not.
  const pickupLocations = useMemo(
    () =>
      (locations.data?.pickupLocations ?? []).map((location) => ({
        id: location.id,
        name: location.name,
      })),
    [locations.data],
  );

  return {
    rows,
    pickupLocations,
    isLoading:
      locations.isLoading ||
      locations.isFetching ||
      locationGroups.isLoading ||
      locationGroups.isFetching ||
      !schedules.loaded,
    error: locations.error ?? locationGroups.error,
    // Kept apart: the site registry still shows without its schedule columns.
    schedulesFailed: schedules.failed,
  };
}
